import { message, Modal } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { OPSUserRoleVO } from "@/api/accounts/type";
import type { OrganizationOrgType } from "@/api/system/organizations/type";
import { batchSaveRoleMenus, batchUpdateUserRoleStatus } from "@/api/system/roles";
import type { RoleBatchMenuSaveItemRequest } from "@/api/system/roles/type";
import type { PermissionSelectionByApp } from "@/components/PermissionTree";
import { ENABLE_STATUS_CONFIG, type EnableStatus } from "@/types";

import { useEntityRoleBatch } from "../../AccountDetail/hooks/useEntityRoleBatch";

/** 判断两组 menuIds 是否一致（忽略顺序） */
const isSameMenuIds = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((id) => rightSet.has(id));
};

/** 统一 systemTypeId 为字符串，避免接口 number/string 导致匹配失败 */
const toSystemTypeId = (id: string | number) => String(id);

/**
 * 权限详情面板：角色批量加载、编辑草稿与保存
 * @param selectedEntityRole 当前选中的身份角色
 * @param userId 当前账号用户 ID，用于绑定状态切换
 * @param onStatusChanged 身份状态切换成功后的回调（刷新账号详情）
 */
export const usePermissionDetailPanel = (
  selectedEntityRole: OPSUserRoleVO | null,
  userId?: string,
  onStatusChanged?: () => void | Promise<void>,
) => {
  // 是否处于编辑模式
  const [isEditing, setIsEditing] = useState(false);
  // 保存提交中
  const [saveLoading, setSaveLoading] = useState(false);
  // 状态切换提交中
  const [statusLoading, setStatusLoading] = useState(false);
  // 保存成功后递增，触发角色详情重新拉取
  const [reloadKey, setReloadKey] = useState(0);
  // 编辑态按端分组的完整权限草稿（含尚未有角色的端，保证勾选可回写）
  const [draftSelection, setDraftSelection] = useState<PermissionSelectionByApp[]>([]);

  const { roles, loading: rolesLoading } = useEntityRoleBatch(selectedEntityRole, reloadKey);

  /** 只读态：由已加载角色拼出按端选择值 */
  const rolesSelection = useMemo<PermissionSelectionByApp[]>(
    () =>
      roles.map((role) => ({
        systemTypeId: toSystemTypeId(role.systemTypeId),
        menuIds: role.menuIds ?? [],
      })),
    [roles],
  );

  /** 权限树受控值：编辑用草稿，只读用角色数据 */
  const permissionValue = isEditing ? draftSelection : rolesSelection;

  /** 切换身份时退出编辑并清空草稿 */
  useEffect(() => {
    setIsEditing(false);
    setDraftSelection([]);
  }, [selectedEntityRole]);

  /** 进入编辑：用当前各端角色 menuIds 初始化草稿 */
  const startEditing = useCallback(() => {
    setDraftSelection(
      roles.map((role) => ({
        systemTypeId: toSystemTypeId(role.systemTypeId),
        menuIds: [...(role.menuIds ?? [])],
      })),
    );
    setIsEditing(true);
  }, [roles]);

  /** 取消编辑：丢弃草稿 */
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setDraftSelection([]);
  }, []);

  /**
   * 整表回写按端选择结果
   * 必须保留 selection 中全部端（含当前 roles 里没有的端），否则空端首次勾选会被丢弃
   */
  const updateDraftSelection = useCallback((selection: PermissionSelectionByApp[]) => {
    setDraftSelection(
      selection.map((item) => ({
        systemTypeId: toSystemTypeId(item.systemTypeId),
        menuIds: item.menuIds,
      })),
    );
  }, []);

  /** 保存有变更的端菜单权限（已有角色更新；新勾选且无角色的端走新建） */
  const saveChanges = useCallback(async () => {
    if (!selectedEntityRole) return;
    // systemTypeId → 已有角色，用于判断更新还是新建
    const roleBySystemTypeId = new Map(
      roles.map((role) => [toSystemTypeId(role.systemTypeId), role]),
    );
    const items: RoleBatchMenuSaveItemRequest[] = draftSelection
      .filter((item) => {
        const role = roleBySystemTypeId.get(toSystemTypeId(item.systemTypeId));
        if (role) {
          return !isSameMenuIds(item.menuIds, role.menuIds ?? []);
        }
        // 无对应角色：仅当该端有勾选时才新建
        return item.menuIds.length > 0;
      })
      .map((item) => {
        const role = roleBySystemTypeId.get(toSystemTypeId(item.systemTypeId));
        return {
          ...(role ? { roleId: role.id } : {}),
          roleType: selectedEntityRole.roleType,
          templateType:
            selectedEntityRole.roleType === "TEMPLATE"
              ? (selectedEntityRole.templateType as OrganizationOrgType)
              : undefined,
          orgId: selectedEntityRole.roleType === "ORG" ? selectedEntityRole.orgId : undefined,
          roleName: role?.roleName ?? selectedEntityRole.roleName,
          roleDesc: role?.roleDesc,
          systemTypeId: toSystemTypeId(item.systemTypeId),
          menuIds: item.menuIds,
        };
      });

    if (items.length === 0) {
      message.info("权限未变更");
      setIsEditing(false);
      setDraftSelection([]);
      return;
    }
    setSaveLoading(true);
    try {
      await batchSaveRoleMenus({ items });
      message.success("权限保存成功");
      setIsEditing(false);
      setDraftSelection([]);
      setReloadKey((key) => key + 1);
      // 可能新建了端角色，刷新账号详情以同步 userRoleInfoList
      await onStatusChanged?.();
    } catch {
      message.error("权限保存失败，请重试");
    } finally {
      setSaveLoading(false);
    }
  }, [draftSelection, onStatusChanged, roles, selectedEntityRole]);

  /** 批量切换该用户下当前身份各端角色绑定状态 */
  const onToggleStatus = useCallback(() => {
    if (!selectedEntityRole || !userId || statusLoading || isEditing) return;
    const roleIds = (selectedEntityRole.userRoleInfoList ?? [])
      .map((item) => item.roleId)
      .filter(Boolean);
    if (roleIds.length === 0) {
      message.warning("暂无可切换的角色");
      return;
    }
    const nextStatus: EnableStatus =
      selectedEntityRole.status === "ENABLED" ? "DISABLED" : "ENABLED";
    const actionLabel = ENABLE_STATUS_CONFIG[nextStatus].label;
    Modal.confirm({
      title: `${actionLabel}身份`,
      content: `确定${actionLabel}「${selectedEntityRole.roleName}」吗？${
        nextStatus === "DISABLED" ? "停用后该身份权限将失效。" : ""
      }`,
      okText: actionLabel,
      okButtonProps: { danger: nextStatus === "DISABLED" },
      onOk: async () => {
        setStatusLoading(true);
        try {
          await batchUpdateUserRoleStatus({ userId, roleIds, status: nextStatus });
          message.success(`已${actionLabel}`);
          setIsEditing(false);
          setDraftSelection([]);
          setReloadKey((key) => key + 1);
          await onStatusChanged?.();
        } catch {
          message.error("状态切换失败，请重试");
        } finally {
          setStatusLoading(false);
        }
      },
    });
  }, [isEditing, onStatusChanged, selectedEntityRole, statusLoading, userId]);

  return {
    roles,
    rolesLoading,
    isEditing,
    saveLoading,
    statusLoading,
    permissionValue,
    startEditing,
    cancelEditing,
    saveChanges,
    updateDraftSelection,
    onToggleStatus,
  };
};
